/**
 * CustomDashboardPage - Main page for custom dashboards
 *
 * Features:
 * - Create/manage custom dashboards as tabs
 * - Drag-and-drop tile arrangement
 * - Add tiles via Data Studio
 * - Export/import dashboard configurations
 */

import { DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import { arrayMove, SortableContext, useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@getdevpulse/shared-ui"
import { Download, FileUp, MoreVertical, Plus, Trash2 } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useCallback, useEffect, useRef, useState } from "react"
import { DashboardTile } from "../components/DashboardTile"
import { DataStudio } from "../components/DataStudio"

// Constants
const DASHBOARD_TILE_HEIGHT = 600 // Height in pixels for dashboard tiles

import type {
  DashboardTile as DashboardTileType,
  VisualizationType,
} from "../stores/CustomDashboardStore"
import { useCustomDashboardStore, useDataStore } from "../stores/StoreContext"

// Sortable tile wrapper for drag-and-drop
const SortableTile = observer(
  ({
    tile,
    onDelete,
    onEdit,
  }: {
    tile: DashboardTileType
    onDelete: () => void
    onEdit: () => void
  }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: tile.id,
    })

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    }

    return (
      <div ref={setNodeRef} style={style} className="h-full" {...attributes} {...listeners}>
        <DashboardTile tile={tile} onDelete={onDelete} onEdit={onEdit} />
      </div>
    )
  },
)

export const CustomDashboardPage = observer(() => {
  const dataStore = useDataStore()
  const customDashboardStore = useCustomDashboardStore()

  const [showDataStudio, setShowDataStudio] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [importJson, setImportJson] = useState("")
  const [showNewDashboardModal, setShowNewDashboardModal] = useState(false)
  const [newDashboardName, setNewDashboardName] = useState("")
  const [newDashboardDescription, setNewDashboardDescription] = useState("")
  const [showEditTileModal, setShowEditTileModal] = useState(false)
  const [editingTile, setEditingTile] = useState<DashboardTileType | null>(null)
  const [editTileName, setEditTileName] = useState("")
  const [editTileQuery, setEditTileQuery] = useState("")
  const [editTileVisualizationType, setEditTileVisualizationType] =
    useState<VisualizationType>("table")
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
  const [tileToDelete, setTileToDelete] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  )

  // Execute queries for all tiles when data changes
  useEffect(() => {
    if (!customDashboardStore.activeDashboard) return

    for (const tile of customDashboardStore.activeDashboard.tiles) {
      try {
        let entityData: Record<string, unknown>[] = []

        switch (tile.parsedQuery.from) {
          case "commits":
            entityData = dataStore.allCommits as unknown as Record<string, unknown>[]
            break
          case "prs":
            entityData = dataStore.allPullRequests as unknown as Record<string, unknown>[]
            break
          case "issues":
            entityData = dataStore.allIssues as unknown as Record<string, unknown>[]
            break
          case "releases":
            entityData = dataStore.allReleases as unknown as Record<string, unknown>[]
            break
        }

        customDashboardStore.executeTileQuery(tile.id, entityData)
      } catch (error) {
        console.error("Failed to execute tile query:", error)
      }
    }
  }, [dataStore, customDashboardStore.activeDashboard])

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      if (!over || active.id === over.id) return

      const dashboard = customDashboardStore.activeDashboard
      if (!dashboard) return

      const oldIndex = dashboard.tiles.findIndex((t) => t.id === active.id)
      const newIndex = dashboard.tiles.findIndex((t) => t.id === over.id)

      if (oldIndex !== -1 && newIndex !== -1) {
        const newTiles = arrayMove(dashboard.tiles, oldIndex, newIndex)

        // Reorder tiles in the store
        customDashboardStore.reorderTiles(newTiles.map((tile) => tile.id))
      }
    },
    [customDashboardStore],
  )

  // Create new dashboard
  const handleCreateDashboard = useCallback(() => {
    if (!newDashboardName.trim()) return

    customDashboardStore.createDashboard(newDashboardName, newDashboardDescription)
    setNewDashboardName("")
    setNewDashboardDescription("")
    setShowNewDashboardModal(false)
  }, [newDashboardName, newDashboardDescription, customDashboardStore])

  // Delete dashboard
  const handleDeleteDashboard = useCallback(
    (dashboardId: string) => {
      if (confirm("Are you sure you want to delete this dashboard?")) {
        customDashboardStore.deleteDashboard(dashboardId)
      }
    },
    [customDashboardStore],
  )

  // Export dashboard
  const handleExport = useCallback(
    (dashboardId: string) => {
      const json = customDashboardStore.exportDashboard(dashboardId)
      if (!json) return

      const dashboard = customDashboardStore.dashboards.get(dashboardId)
      const blob = new Blob([json], { type: "application/json" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `dashboard-${dashboard?.name || "export"}-${Date.now()}.json`
      a.click()
      URL.revokeObjectURL(url)
    },
    [customDashboardStore],
  )

  // Import dashboard from JSON
  const handleImport = useCallback(() => {
    if (!importJson.trim()) return

    const dashboardId = customDashboardStore.importDashboard(importJson)
    if (dashboardId) {
      setImportJson("")
      setShowImportModal(false)
    }
  }, [importJson, customDashboardStore])

  // Import from file
  const handleImportFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const json = event.target?.result as string
      setImportJson(json)
      setShowImportModal(true)
    }
    reader.readAsText(file)
  }, [])

  // Handle edit tile
  const handleEditTile = useCallback((tile: DashboardTileType) => {
    setEditingTile(tile)
    setEditTileName(tile.title)
    setEditTileQuery(tile.query)
    setEditTileVisualizationType(tile.visualizationType)
    setShowEditTileModal(true)
  }, [])

  // Save edited tile
  const handleSaveEditedTile = useCallback(() => {
    if (!editingTile) return

    customDashboardStore.updateTile(editingTile.id, {
      title: editTileName,
      query: editTileQuery,
      visualizationType: editTileVisualizationType,
    })

    // Re-execute the query if it was changed
    if (editTileQuery !== editingTile.query) {
      try {
        // Get the updated tile from store to use the newly parsed query
        const updatedTile = customDashboardStore.activeDashboard?.tiles.find(
          (t) => t.id === editingTile.id,
        )

        if (updatedTile?.parsedQuery) {
          let entityData: Record<string, unknown>[] = []

          switch (updatedTile.parsedQuery.from) {
            case "commits":
              entityData = dataStore.allCommits as unknown as Record<string, unknown>[]
              break
            case "prs":
              entityData = dataStore.allPullRequests as unknown as Record<string, unknown>[]
              break
            case "issues":
              entityData = dataStore.allIssues as unknown as Record<string, unknown>[]
              break
            case "releases":
              entityData = dataStore.allReleases as unknown as Record<string, unknown>[]
              break
          }

          customDashboardStore.executeTileQuery(editingTile.id, entityData)
        }
      } catch (error) {
        console.error("Failed to re-execute query:", error)
      }
    }

    setShowEditTileModal(false)
    setEditingTile(null)
  }, [
    editingTile,
    editTileName,
    editTileQuery,
    editTileVisualizationType,
    customDashboardStore,
    dataStore,
  ])

  // Handle delete tile with confirmation
  const handleDeleteTile = useCallback((tileId: string) => {
    setTileToDelete(tileId)
    setShowDeleteConfirmModal(true)
  }, [])

  // Confirm delete tile
  const handleConfirmDeleteTile = useCallback(() => {
    if (tileToDelete) {
      customDashboardStore.deleteTile(tileToDelete)
    }
    setShowDeleteConfirmModal(false)
    setTileToDelete(null)
  }, [tileToDelete, customDashboardStore])

  // Render dashboard tab content
  const renderDashboardContent = useCallback(
    (dashboardId: string) => {
      const dashboard = customDashboardStore.dashboards.get(dashboardId)
      if (!dashboard) return null

      return (
        <div className="p-4">
          {dashboard.tiles.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-muted-foreground mb-4">
                  No tiles yet. Click "Add Tile" in the menu to create your first report.
                </p>
              </div>
            </div>
          ) : (
            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
              <SortableContext items={dashboard.tiles.map((t) => t.id)}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {dashboard.tiles.map((tile) => (
                    <div key={tile.id} style={{ height: `${DASHBOARD_TILE_HEIGHT}px` }}>
                      <SortableTile
                        tile={tile}
                        onDelete={() => handleDeleteTile(tile.id)}
                        onEdit={() => handleEditTile(tile)}
                      />
                    </div>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )
    },
    [customDashboardStore, sensors, handleDragEnd],
  )

  // If no dashboards exist, show create prompt
  if (customDashboardStore.dashboardsList.length === 0) {
    return (
      <div className="container mx-auto p-3 sm:p-4 md:p-6">
        <PageHeader title="Custom Dashboard" />

        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-bold mb-4">No Dashboards Yet</h2>
            <p className="text-muted-foreground mb-6">
              Create your first custom dashboard to start building personalized reports and
              visualizations.
            </p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => setShowNewDashboardModal(true)}>
                <Plus className="h-4 w-4" />
                Create Dashboard
              </Button>
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                <FileUp className="h-4 w-4" />
                Import Dashboard
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImportFile}
              className="hidden"
            />
          </div>
        </div>

        {/* New Dashboard Modal */}
        {showNewDashboardModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-background rounded-lg shadow-xl max-w-md w-full p-6 border">
              <h3 className="text-xl font-semibold mb-4">Create New Dashboard</h3>

              <div className="mb-4">
                <label htmlFor="dashboardName" className="block text-sm font-medium mb-1">
                  Dashboard Name
                </label>
                <input
                  type="text"
                  id="dashboardName"
                  value={newDashboardName}
                  onChange={(e) => setNewDashboardName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  placeholder="My Custom Dashboard"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="dashboardDescription" className="block text-sm font-medium mb-1">
                  Description (optional)
                </label>
                <textarea
                  id="dashboardDescription"
                  value={newDashboardDescription}
                  onChange={(e) => setNewDashboardDescription(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  rows={3}
                  placeholder="Dashboard description..."
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowNewDashboardModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateDashboard} disabled={!newDashboardName.trim()}>
                  Create
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="container mx-auto p-3 sm:p-4 md:p-6">
      <PageHeader
        title="Custom Dashboard"
        rightContent={
          <div className="flex gap-2">
            <Button onClick={() => setShowNewDashboardModal(true)}>
              <Plus className="h-4 w-4" />
              New Dashboard
            </Button>
            <Button variant="outline" onClick={() => setShowImportModal(true)}>
              <FileUp className="h-4 w-4" />
              Import
            </Button>
          </div>
        }
      />

      <Tabs
        value={customDashboardStore.activeDashboardId || undefined}
        onValueChange={(tabId) => customDashboardStore.setActiveDashboard(tabId)}
      >
        <TabsList>
          {customDashboardStore.dashboardsList.map((dashboard) => (
            <TabsTrigger key={dashboard.id} value={dashboard.id} className="group">
              <div className="flex items-center gap-2">
                <span>{dashboard.name}</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <span className="inline-flex items-center justify-center h-5 w-5 p-0 opacity-0 group-hover:opacity-100 group-data-[state=active]:opacity-100 transition-opacity cursor-pointer">
                      <MoreVertical className="h-3.5 w-3.5" />
                    </span>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowDataStudio(true)
                      }}
                    >
                      <Plus className="h-4 w-4" />
                      Add Tile
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation()
                        handleExport(dashboard.id)
                      }}
                    >
                      <Download className="h-4 w-4" />
                      Export
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteDashboard(dashboard.id)
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </TabsTrigger>
          ))}
        </TabsList>

        {customDashboardStore.dashboardsList.map((dashboard) => (
          <TabsContent key={dashboard.id} value={dashboard.id}>
            {renderDashboardContent(dashboard.id)}
          </TabsContent>
        ))}
      </Tabs>

      {/* Data Studio Modal */}
      {showDataStudio && <DataStudio onClose={() => setShowDataStudio(false)} />}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-2xl w-full p-6 border">
            <h3 className="text-xl font-semibold mb-4">Import Dashboard</h3>

            <div className="mb-4">
              <label htmlFor="importJson" className="block text-sm font-medium mb-1">
                Paste Dashboard JSON
              </label>
              <textarea
                id="importJson"
                value={importJson}
                onChange={(e) => setImportJson(e.target.value)}
                className="w-full h-64 px-3 py-2 font-mono text-xs border rounded-md bg-background"
                placeholder='{"id": "...", "name": "...", ...}'
              />
            </div>

            <div className="flex justify-between items-center">
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                Or select file...
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImportFile}
                className="hidden"
              />

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowImportModal(false)
                    setImportJson("")
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleImport} disabled={!importJson.trim()}>
                  Import
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Dashboard Modal */}
      {showNewDashboardModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-md w-full p-6 border">
            <h3 className="text-xl font-semibold mb-4">Create New Dashboard</h3>

            <div className="mb-4">
              <label htmlFor="dashboardName" className="block text-sm font-medium mb-1">
                Dashboard Name
              </label>
              <input
                type="text"
                id="dashboardName"
                value={newDashboardName}
                onChange={(e) => setNewDashboardName(e.target.value)}
                className="w-full px-3 py-2 border rounded-md bg-background"
                placeholder="My Custom Dashboard"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="dashboardDescription" className="block text-sm font-medium mb-1">
                Description (optional)
              </label>
              <textarea
                id="dashboardDescription"
                value={newDashboardDescription}
                onChange={(e) => setNewDashboardDescription(e.target.value)}
                className="w-full px-3 py-2 border rounded-md bg-background"
                rows={3}
                placeholder="Dashboard description..."
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNewDashboardModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateDashboard} disabled={!newDashboardName.trim()}>
                Create
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tile Modal */}
      {showEditTileModal && editingTile && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-2xl w-full p-6 border">
            <h3 className="text-xl font-semibold mb-4">Edit Tile</h3>

            <div className="mb-4">
              <label htmlFor="editTileName" className="block text-sm font-medium mb-1">
                Tile Name
              </label>
              <input
                type="text"
                id="editTileName"
                value={editTileName}
                onChange={(e) => setEditTileName(e.target.value)}
                className="w-full px-3 py-2 border rounded-md bg-background"
                placeholder="Tile name"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="editTileQuery" className="block text-sm font-medium mb-1">
                Query
              </label>
              <textarea
                id="editTileQuery"
                value={editTileQuery}
                onChange={(e) => setEditTileQuery(e.target.value)}
                className="w-full h-32 px-3 py-2 font-mono text-sm border rounded-md bg-background resize-none"
                placeholder="SELECT * FROM commits WHERE ..."
              />
            </div>

            <div className="mb-4">
              <label htmlFor="editVisualizationType" className="block text-sm font-medium mb-1">
                Visualization Type
              </label>
              <Select
                value={editTileVisualizationType}
                onValueChange={(value) => setEditTileVisualizationType(value as VisualizationType)}
              >
                <SelectTrigger id="editVisualizationType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="table">Table</SelectItem>
                  <SelectItem value="bar">Bar Chart</SelectItem>
                  <SelectItem value="line">Line Chart</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditTileModal(false)
                  setEditingTile(null)
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveEditedTile} disabled={!editTileName.trim()}>
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-md w-full p-6 border">
            <h3 className="text-xl font-semibold mb-4">Confirm Delete</h3>

            <p className="text-muted-foreground mb-6">
              Are you sure you want to delete this tile? This action cannot be undone.
            </p>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirmModal(false)
                  setTileToDelete(null)
                }}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirmDeleteTile}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
})
